import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockIcon from "@material-ui/icons/Lock";
import offerApi from "../../services/offerApi";
import { useOfferCheckoutStyles } from "./offerCheckout.styles";
import SupportContact from "./SupportContact";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import useOfferAnalyticsScripts from "./hooks/useOfferAnalyticsScripts";

/**
 * Post-payment shipping capture.
 *
 * States:
 *   - loading     → fetching context
 *   - not-paid    → 403 from backend (payment not yet captured by webhook)
 *   - already-done → shipping was already submitted on a prior visit or now
 *   - form        → show address form
 *   - submitting  → POST in flight
 */

const STATES_OF_INDIA = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep",
];

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.aisensy.mobile&pcampaignid=web_share";
const APP_STORE_URL =
  "https://apps.apple.com/in/app/aisensy/id6711351197";

const EMPTY_FORM = {
  email: "",
  line1: "",
  line2: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
};

const ShippingPage = () => {
  const classes = useOfferCheckoutStyles();
  useOfferAnalyticsScripts();
  const { offerSlug, customerId } = useParams();

  const [phase, setPhase] = useState("loading"); // loading | form | already-done | forbidden | not-found | error
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [pincodeLooking, setPincodeLooking] = useState(false);
  const [submittedAddress, setSubmittedAddress] = useState(null);
  const [shopifyOrderName, setShopifyOrderName] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "error",
  });

  // Used to make sure a late pincode lookup doesn't clobber a newer one.
  const pincodeRequestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await offerApi.getShippingContext(customerId);
        if (cancelled) return;
        setContext(data);
        if (data.digitalOnly) {
          setPhase("already-done");
          return;
        }
        // Prefill email if the server already has a real one. Skip the
        // synthetic placeholder pattern the old code used to create
        // (e.g. "9876543210@offers.aisensy.local") so we never default
        // a user into a fake email on a reused customer record.
        const prefillEmail = data?.prefill?.email;
        if (prefillEmail && !/@offers\.aisensy\.local$/i.test(prefillEmail)) {
          setForm((f) => ({ ...f, email: prefillEmail }));
        }
        if (data.shippingStatus === "submitted") {
          setShopifyOrderName(data.shopifyOrderName || null);
          // If they refresh /shipping/:id after a prior submit, show the
          // saved address in the confirmation card instead of nothing.
          if (data.shippingAddress) {
            setSubmittedAddress(data.shippingAddress);
          }
          setPhase("already-done");
        } else {
          setPhase("form");
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403) setPhase("forbidden");
        else if (err?.status === 404) setPhase("not-found");
        else setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const setField = (k) => (ev) => {
    let v = ev.target.value;
    if (k === "pincode") v = v.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  // Fire pincode lookup on blur. Non-blocking: failures are silent and
  // don't stop submission. Always overwrites city + state with the
  // lookup result — previously we preserved user edits, but that made
  // re-entering a new pincode leave stale city/state on screen.
  const handlePincodeBlur = async () => {
    const pin = form.pincode.trim();
    if (!/^\d{6}$/.test(pin)) return;
    const reqId = ++pincodeRequestId.current;
    setPincodeLooking(true);
    const result = await offerApi.lookupPincode(pin);
    if (reqId !== pincodeRequestId.current) {
      // A newer lookup is in flight; ignore this stale response.
      return;
    }
    setPincodeLooking(false);
    if (!result) return;
    setForm((f) => ({
      ...f,
      city: result.city,
      state: result.state,
    }));
    // Clear any prior city / state validation errors.
    setErrors((er) => ({ ...er, city: undefined, state: undefined }));
  };

  const validate = () => {
    const e = {};
    const emailTrimmed = form.email.trim();
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed))
      e.email = "Enter a valid email address";
    if (!form.line1.trim() || form.line1.trim().length < 3)
      e.line1 = "Address line 1 is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state.trim()) e.state = "State is required";
    if (!/^\d{6}$/.test(form.pincode.trim()))
      e.pincode = "Enter a valid 6-digit PIN code";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    if (ev) ev.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const address = {
      line1: form.line1.trim(),
      line2: form.line2.trim() || undefined,
      landmark: form.landmark.trim() || undefined,
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: "IN",
    };
    try {
      const resp = await offerApi.submitShipping(customerId, {
        email: form.email.trim(),
        address,
      });
      setShopifyOrderName(resp.shopifyOrderName || null);
      setSubmittedAddress(address);
      setPhase("already-done");
    } catch (err) {
      console.error("[ShippingPage] submit failed:", err);
      const fieldErrs = err?.data?.errors || err?.errors;
      if (fieldErrs && typeof fieldErrs === "object") {
        const mapped = {};
        for (const [k, v] of Object.entries(fieldErrs)) {
          if (!v) continue;
          const flatKey = k.replace(/^address\./, "");
          mapped[flatKey] = String(v);
        }
        if (Object.keys(mapped).length > 0) setErrors((er) => ({ ...er, ...mapped }));
      }
      setToast({
        open: true,
        severity: "error",
        msg:
          err?.message ||
          "We couldn't save your shipping details. Please try again.",
      });
      setSubmitting(false);
    }
  };

  const prefillName = useMemo(() => context?.prefill?.name || "", [context]);

  // Pretty "+91 8064124248" for the confirmation card. Falls back to bare
  // digits if no dial code is on record.
  const displayPhone = useMemo(() => {
    const phone = context?.prefill?.phone || "";
    if (!phone) return "";
    const cc = context?.prefill?.countryCallingCode || "";
    return cc ? `${cc} ${phone}` : phone;
  }, [context]);

  // Show the email the user just submitted (form.email), falling back to
  // whatever was on record (e.g. after a page refresh post-submit).
  const displayEmail = useMemo(
    () => form.email || context?.prefill?.email || "",
    [form.email, context]
  );

  /**
   * Builds the "Paid ₹X · <plan title> (<billing>)" line from whatever
   * the context endpoint returned. Every segment is optional — if a
   * field is missing we render what we have rather than literal "null".
   */
  const receiptLine = useMemo(() => {
    if (!context) return "";
    const parts = [];
    const amount = context.amountPaid;
    const currency = context.currency === "INR" ? "₹" : context.currency;
    if (amount != null) {
      parts.push(
        `Paid ${currency}${Number(amount).toLocaleString("en-IN")}`
      );
    }
    const planLabel = [context.plan?.title, context.plan?.billingPeriod]
      .filter(Boolean)
      .join(" · ");
    if (planLabel) parts.push(planLabel);
    return parts.join(" · ");
  }, [context]);

  /* ---------- render ---------- */

  if (phase === "loading") {
    return (
      <div className={classes.loadingRoot}>
        <img src={AiSensyLogo} alt="AiSensy" className={classes.loadingLogo} />
        <CircularProgress size={28} style={{ color: "#04cf66" }} />
      </div>
    );
  }

  if (phase === "forbidden" || phase === "not-found" || phase === "error") {
    const isForbidden = phase === "forbidden";
    const isMissing = phase === "not-found";
    return (
      <div className={classes.errorRoot}>
        <div className={classes.errorCard}>
          <Typography className={classes.errorHeading}>
            {isForbidden
              ? "We couldn't verify your payment"
              : isMissing
              ? "We couldn't find your order"
              : "Something went wrong"}
          </Typography>
          <Typography className={classes.errorMessage}>
            {isForbidden
              ? "If you just paid, please wait a few seconds and reload. If the problem persists, contact support."
              : isMissing
              ? "This link may be incorrect. Contact support with your payment reference."
              : "Please try again in a minute."}
          </Typography>
          <Button
            variant="contained"
            className={classes.retryButton}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
          <SupportContact compact />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageRoot}>
      <div className={classes.pageHeader}>
        <img src={AiSensyLogo} alt="AiSensy" className={classes.pageLogo} />
      </div>

      <div className={classes.pageContainer}>
        {phase === "already-done" ? (
          <>
            {/* ---------- confirmation card ---------- */}
            <div className={classes.formCard} style={{ textAlign: "center" }}>
              <Typography
                className={classes.formTitle}
                style={{ textAlign: "center", marginTop: 4 }}
              >
                🎉 Your order is confirmed
                {prefillName ? `, ${prefillName.split(" ")[0]}!` : "!"}
              </Typography>

              {shopifyOrderName && (
                <Typography
                  variant="body2"
                  style={{
                    color: "#1a1a1a",
                    fontWeight: 600,
                    marginTop: 8,
                  }}
                >
                  Order {shopifyOrderName}
                </Typography>
              )}
              {receiptLine && (
                <Typography
                  variant="body2"
                  style={{ color: "#696969", fontSize: 13, marginBottom: 16 }}
                >
                  {receiptLine}
                </Typography>
              )}

              {submittedAddress && (
                <div
                  style={{
                    backgroundColor: "#fafbfd",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                    textAlign: "left",
                    fontSize: 13,
                    color: "#374151",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    🚚 Shipping to
                  </div>
                  <div>{submittedAddress.line1}</div>
                  {submittedAddress.line2 && <div>{submittedAddress.line2}</div>}
                  {submittedAddress.landmark && (
                    <div>{submittedAddress.landmark}</div>
                  )}
                  <div>
                    {submittedAddress.city}, {submittedAddress.state}{" "}
                    {submittedAddress.pincode}
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: "1px dashed #e5e7eb",
                      color: "#047857",
                      fontWeight: 500,
                      fontSize: 12,
                    }}
                  >
                    📦 QR Kit delivered in 4–7 days
                  </div>
                </div>
              )}

              <div
                style={{
                  textAlign: "left",
                  fontSize: 13,
                  color: "#1f2937",
                  backgroundColor: "#f0fbf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 8,
                  padding: "14px 14px 12px",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: "#047857",
                    marginBottom: 10,
                  }}
                >
                  Next steps
                </div>

                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 18, lineHeight: "22px" }}>💬</div>
                  <div style={{ flex: 1, lineHeight: "20px" }}>
                    Your access code is on its way to your WhatsApp
                    {displayPhone && (
                      <>
                        {" "}
                        <strong>{displayPhone}</strong>
                      </>
                    )}
                    {displayEmail && (
                      <>
                        {" "}and email <strong>{displayEmail}</strong>
                      </>
                    )}
                    .
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 18, lineHeight: "22px" }}>📲</div>
                  <div style={{ flex: 1, lineHeight: "20px" }}>
                    Download the AiSensy app from the links below.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ fontSize: 18, lineHeight: "22px" }}>🔑</div>
                  <div style={{ flex: 1, lineHeight: "20px" }}>
                    Paste the code into the <strong>"Access Code"</strong>{" "}
                    section on the dashboard — your plan will be activated
                    instantly.
                  </div>
                </div>
              </div>

              {/* Official Play Store + App Store badges. These are public
                  Google/Apple marketing assets; clicking opens the native
                  store in a new tab / the store app on mobile. */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 4,
                  flexWrap: "wrap",
                }}
              >
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Get it on Google Play"
                  style={{ display: "inline-block", lineHeight: 0 }}
                >
                  <img
                    alt="Get it on Google Play"
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    style={{ height: 36, display: "block" }}
                  />
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Download on the App Store"
                  style={{ display: "inline-block", lineHeight: 0 }}
                >
                  <img
                    alt="Download on the App Store"
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    style={{ height: 36, display: "block" }}
                  />
                </a>
              </div>
            </div>
            <SupportContact />
          </>
        ) : (
          <>
            {/* ---------- shipping form ---------- */}
            <form
              className={classes.formCard}
              onSubmit={handleSubmit}
              noValidate
            >
              <Typography className={classes.formTitle}>
                Shipping details
              </Typography>
              <Typography
                variant="body2"
                style={{
                  color: "#696969",
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                Where should we ship your <strong>QR Kit</strong>? (India only)
              </Typography>

              <Typography variant="body2" className={classes.formFieldLabel}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                name="email"
                type="email"
                placeholder="you@business.com"
                value={form.email}
                onChange={setField("email")}
                error={Boolean(errors.email)}
                helperText={errors.email}
                className={classes.formField}
                InputLabelProps={{ shrink: false }}
                autoComplete="email"
                inputProps={{ inputMode: "email" }}
              />

              <Typography variant="body2" className={classes.formFieldLabel}>
                Address line 1*
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                name="line1"
                placeholder="House / flat / building"
                value={form.line1}
                onChange={setField("line1")}
                error={Boolean(errors.line1)}
                helperText={errors.line1}
                className={classes.formField}
                InputLabelProps={{ shrink: false }}
                autoComplete="address-line1"
              />

              <Typography variant="body2" className={classes.formFieldLabel}>
                Address line 2
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                name="line2"
                placeholder="Street / area"
                value={form.line2}
                onChange={setField("line2")}
                className={classes.formField}
                InputLabelProps={{ shrink: false }}
                autoComplete="address-line2"
              />

              <Typography variant="body2" className={classes.formFieldLabel}>
                Landmark
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                name="landmark"
                placeholder="Near a well-known place (optional)"
                value={form.landmark}
                onChange={setField("landmark")}
                className={classes.formField}
                InputLabelProps={{ shrink: false }}
              />

              {/* pincode first so autofill can populate city/state */}
              <Typography variant="body2" className={classes.formFieldLabel}>
                PIN code*
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                name="pincode"
                placeholder="110001"
                value={form.pincode}
                onChange={setField("pincode")}
                onBlur={handlePincodeBlur}
                error={Boolean(errors.pincode)}
                helperText={errors.pincode}
                className={classes.formField}
                InputLabelProps={{ shrink: false }}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                autoComplete="postal-code"
              />

              <Box display="flex" style={{ gap: 12 }}>
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    className={classes.formFieldLabel}
                  >
                    City*
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    required
                    name="city"
                    placeholder={pincodeLooking ? "Fetching..." : "City"}
                    value={form.city}
                    onChange={setField("city")}
                    error={Boolean(errors.city)}
                    helperText={errors.city}
                    className={classes.formField}
                    InputLabelProps={{ shrink: false }}
                    autoComplete="address-level2"
                    InputProps={{
                      endAdornment: pincodeLooking ? (
                        <InputAdornment position="end">
                          <CircularProgress
                            size={16}
                            style={{ color: "#04cf66" }}
                          />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    className={classes.formFieldLabel}
                  >
                    State*
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    SelectProps={{
                      native: true,
                      IconComponent: pincodeLooking
                        ? () => (
                            <CircularProgress
                              size={16}
                              style={{ color: "#04cf66", marginRight: 8 }}
                            />
                          )
                        : undefined,
                    }}
                    variant="outlined"
                    required
                    name="state"
                    value={form.state}
                    onChange={setField("state")}
                    error={Boolean(errors.state)}
                    helperText={errors.state}
                    className={classes.formField}
                    InputLabelProps={{ shrink: false }}
                    disabled={pincodeLooking}
                  >
                    <option value="">
                      {pincodeLooking ? "Fetching..." : "Select"}
                    </option>
                    {STATES_OF_INDIA.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </TextField>
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                className={classes.submitButton}
                disabled={submitting}
                startIcon={
                  !submitting ? <LockIcon style={{ fontSize: 18 }} /> : undefined
                }
              >
                {submitting ? (
                  <CircularProgress size={22} style={{ color: "#ffffff" }} />
                ) : (
                  "Confirm Shipping"
                )}
              </Button>

              <Typography
                variant="body2"
                style={{
                  fontSize: 12,
                  color: "#696969",
                  textAlign: "center",
                  marginTop: 12,
                }}
              >
                Please enter the correct address - your QR Kit ships here.
              </Typography>
            </form>
            <SupportContact />
          </>
        )}
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ShippingPage;
